import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Plus, Trash2, X } from "lucide-react";
import { Button, Card, Skeleton } from "@wedding/ui";
import type { Workspace } from "@wedding/schemas";
import { api, apiFetch } from "../api";

type WorkspaceWithRole = Workspace & {
  currentRole?: "owner" | "admin" | "editor" | "viewer";
};

const initialForm = {
  projectName: "",
  brideName: "",
  groomName: "",
  weddingDate: "",
  city: "",
  guestTarget: 0,
  tableCapacity: 10,
  budgetLimitYuan: 0,
};

export function WorkspacesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<Workspace>();
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [form, setForm] = useState(initialForm);
  const workspaces = useQuery({
    queryKey: ["workspaces"],
    queryFn: () =>
      api.workspaces().then((result) => result.data as WorkspaceWithRole[]),
  });
  const create = useMutation({
    mutationFn: () =>
      apiFetch<Workspace>("/workspaces", {
        method: "POST",
        body: JSON.stringify({
          settings: {
            projectName: form.projectName,
            brideName: form.brideName,
            groomName: form.groomName,
            weddingDate: form.weddingDate,
            city: form.city,
            guestTarget: form.guestTarget,
            tableCapacity: form.tableCapacity,
            budgetLimitCents: Math.round(form.budgetLimitYuan * 100),
            preparationStartDate: new Date().toISOString().slice(0, 10),
            timezone: "Asia/Shanghai",
            currency: "CNY",
            region: "中国",
            ceremonyStyle: "酒店婚礼",
            tableCount: 0,
            emergencyFundRate: 0.1,
            primaryColor: "#B69A64",
            secondaryColors: ["#F7F0E3", "#8A9A7B"],
            monogram: "囍",
            status: "未开始",
            bufferDays: 10,
          },
        }),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      setOpen(false);
      setForm(initialForm);
      await navigate({
        to: "/w/$workspaceId/dashboard",
        params: { workspaceId: result.id },
      });
    },
  });
  const remove = useMutation({
    mutationFn: (workspace: Workspace) =>
      apiFetch(`/workspaces/${workspace.id}`, {
        method: "DELETE",
        body: JSON.stringify({ confirmation: workspace.id }),
      }),
    onSuccess: async () => {
      setDeleting(undefined);
      setDeleteConfirmation("");
      await queryClient.invalidateQueries({ queryKey: ["workspaces"] });
    },
  });

  return (
    <div className="workspace-page">
      <header>
        <div className="auth-brand">
          <span>囍</span>
          <strong>知禧</strong>
        </div>
        <div className="avatar">新人</div>
      </header>
      <main>
        <span className="eyebrow">我的婚礼项目</span>
        <h1>选择婚礼项目</h1>
        <p>每个新项目都从完全空白开始，进入各业务模块后从对应待办库按需选择。</p>
        {workspaces.isLoading ? (
          <Skeleton />
        ) : (
          <div className="workspace-grid">
            {workspaces.data?.map((workspace) => (
              <article className="workspace-entry" key={workspace.id}>
                <Link
                  to="/w/$workspaceId/dashboard"
                  params={{ workspaceId: workspace.id }}
                >
                  <Card>
                  <div className="workspace-monogram">
                    {workspace.settings.monogram}
                  </div>
                  <div>
                    <span>{workspace.settings.status}</span>
                    <h2>{workspace.settings.projectName}</h2>
                    <p>
                      {workspace.settings.brideName} &{" "}
                      {workspace.settings.groomName}
                    </p>
                    <time>{workspace.settings.weddingDate}</time>
                  </div>
                    <ArrowRight />
                  </Card>
                </Link>
                {["owner", "admin"].includes(workspace.currentRole ?? "owner") && <button className="workspace-delete" aria-label={`删除项目 ${workspace.settings.projectName}`} onClick={() => { setDeleting(workspace); setDeleteConfirmation(""); }}><Trash2 size={16}/></button>}
              </article>
            ))}
            <button className="new-workspace" onClick={() => { setForm(initialForm); setOpen(true); }}>
              <Plus />
              <strong>创建空白婚礼项目</strong>
              <span>不预置任务、供应商、预算或宾客</span>
            </button>
          </div>
        )}
      </main>
      {open && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="创建空白婚礼项目"
          >
            <header>
              <div>
                <span className="eyebrow">新建婚礼</span>
                <h2>创建空白婚礼项目</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setOpen(false)}
                aria-label="关闭"
              >
                <X />
              </button>
            </header>
            <div className="notice">
              项目创建后所有业务清单都是空的。进入任一业务模块，即可从对应待办库挑选事项。
            </div>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                create.mutate();
              }}
            >
              <div className="form-grid">
                <label className="span-2">
                  <span>项目名称</span>
                  <input
                    required
                    autoFocus
                    placeholder="例如：小雨和阿泽的婚礼"
                    value={form.projectName}
                    onChange={(event) =>
                      setForm({ ...form, projectName: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>新娘姓名</span>
                  <input
                    required
                    placeholder="填写真实姓名"
                    value={form.brideName}
                    onChange={(event) =>
                      setForm({ ...form, brideName: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>新郎姓名</span>
                  <input
                    required
                    placeholder="填写真实姓名"
                    value={form.groomName}
                    onChange={(event) =>
                      setForm({ ...form, groomName: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>婚礼日期</span>
                  <input
                    placeholder="例如：杭州"
                    required
                    type="date"
                    value={form.weddingDate}
                    onChange={(event) =>
                      setForm({ ...form, weddingDate: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>预计宾客人数</span>
                  <input type="number" min="0" value={form.guestTarget} onChange={(event) => setForm({ ...form, guestTarget: Number(event.target.value) })} />
                </label>
                <label>
                  <span>每桌人数</span>
                  <input type="number" min="1" value={form.tableCapacity} onChange={(event) => setForm({ ...form, tableCapacity: Number(event.target.value) })} />
                </label>
                <label className="span-2">
                  <span>婚礼总预算（元，可稍后填写）</span>
                  <input type="number" min="0" step="1000" value={form.budgetLimitYuan} onChange={(event) => setForm({ ...form, budgetLimitYuan: Number(event.target.value) })} />
                </label>
                <label>
                  <span>婚礼城市</span>
                  <input
                    value={form.city}
                    onChange={(event) =>
                      setForm({ ...form, city: event.target.value })
                    }
                  />
                </label>
              </div>
              {create.error && (
                <p className="form-error">{create.error.message}</p>
              )}
              <footer>
                <Button
                  type="button"
                  className="secondary"
                  onClick={() => setOpen(false)}
                >
                  取消
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? "正在创建…" : "创建空白项目"}
                </Button>
              </footer>
            </form>
          </aside>
        </div>
      )}
      {deleting && <div className="drawer-backdrop"><aside className="drawer" role="dialog" aria-modal="true" aria-label="删除婚礼项目"><header><div><span className="eyebrow">删除项目</span><h2>删除婚礼项目</h2></div><button className="icon-button" onClick={() => setDeleting(undefined)} aria-label="关闭"><X/></button></header><form onSubmit={(event) => { event.preventDefault(); remove.mutate(deleting); }}><div className="delete-warning"><Trash2/><div><strong>此操作无法撤销</strong><p>将删除「{deleting.settings.projectName}」的全部待办、预算、供应商、宾客及设置。</p></div></div><label className="delete-confirm-field"><span>请输入项目名称「{deleting.settings.projectName}」确认</span><input autoFocus value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)}/></label>{remove.error&&<p className="form-error">{remove.error.message}</p>}<footer><Button type="button" className="secondary" onClick={() => setDeleting(undefined)}>取消</Button><Button type="submit" className="danger" disabled={deleteConfirmation!==deleting.settings.projectName||remove.isPending}>{remove.isPending?"正在删除…":"永久删除项目"}</Button></footer></form></aside></div>}
    </div>
  );
}
